import {
  Alert,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  Grid,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { Input, SubmitButton } from "components";
import { ModelPropertyBase, UserRegister, UserRegisterViewResource } from "generated/openApi";
import produce from "immer";
import { the404PageRoute } from "pages/The404Page/The404Page";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { loginPageRoute } from "../LoginPage/LoginPage";

const routeName = "userRegister";

class Params {
  tenantName: string | undefined = undefined;
  userId: number | undefined = undefined;
  validationToken: string | undefined = undefined;
}

export const userRegisterPageRoute: PageRoute<Params> = {
  handle: "User Registration",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const UserRegisterPage = () => {
  const { params } = useParams(routeName, Params);

  const theme = useTheme();
  const dispatch = useDispatch();

  const [userRegister, setUserRegister] = useState<UserRegister>({} as UserRegister);
  const [viewResource, setViewResource] = useState<UserRegisterViewResource>({});
  const properties = viewResource.properties || [];
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const navigate = useNavigate();

  useEffect(() => {
    fetch();
    fetchViewResource();
  }, []);

  const fetch = () => {
    ApiService.auth
      .getUserRegister(params.tenantName, params.userId, params.validationToken)
      .then((response) => {
        setUserRegister(response);
      })
      .catch((ex) => {
        navigate({
          pathname: `/${the404PageRoute.getPath()}`,
        });
      });
  };

  const fetchViewResource = () => {
    ApiService.auth.getUserRegisterViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const post = () => {
    setValidationErrors({});

    ApiService.auth
      .postUserRegister(userRegister)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));

        navigate({
          pathname: `/${loginPageRoute.getPath()}`,
        });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const tenantNameField = () => {
    const propertyName = pn<UserRegister>("tenantName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.tenantName}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.tenantName = e.target.value;
            })
          )
        }
        disabled={true}
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<UserRegister>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.email}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.email = e.target.value;
            })
          )
        }
        disabled={true}
      />
    );
  };

  const usernameField = () => {
    const propertyName = pn<UserRegister>("username");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.username}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.username = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<UserRegister>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.firstName}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const lastNameField = () => {
    const propertyName = pn<UserRegister>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.lastName}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const passwordField = () => {
    const propertyName = pn<UserRegister>("password");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.password}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.password = e.target.value;
            })
          )
        }
        type="password"
      />
    );
  };

  const confirmPasswordField = () => {
    const propertyName = pn<UserRegister>("confirmPassword");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={userRegister?.confirmPassword}
        onChange={(e) =>
          setUserRegister(
            produce((draft) => {
              draft.confirmPassword = e.target.value;
            })
          )
        }
        type="password"
      />
    );
  };

  return (
    <Page>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Card sx={{ maxWidth: "550px", width: "100%" }}>
          <Stack px={3} py={7} spacing="lg">
            <Stack spacing="sm" alignItems="center">
              <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                Register for a New Account
              </Typography>
            </Stack>

            <Form>
              <Stack spacing="md">
                {validationErrors?.[pn<UserRegister>("generalValidationErrors")] && (
                  <Alert severity="error">{validationErrors?.[pn<UserRegister>("generalValidationErrors")]?.[0]}</Alert>
                )}

                {tenantNameField()}
                {emailField()}
                {usernameField()}
                {firstNameField()}
                {lastNameField()}
                {passwordField()}
                {confirmPasswordField()}

                <SubmitButton onClick={post} label="Register" fullWidth sx={{ height: "50px" }} />
              </Stack>
            </Form>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};
